import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CATEGORIES, MENU, COMPONI } from "@/data/menuData";
import { Bike, Maximize2, Wheat } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export default function MenuSection() {
  const [cat, setCat] = useState("tutte");
  const items = cat === "tutte" ? MENU : MENU.filter((m) => m.cat === cat);

  return (
    <section id="menu" data-testid="menu-section" className="relative py-28 sm:py-40 bg-coal border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: EASE }}
              className="text-[11px] tracking-[0.45em] uppercase text-terra"
            >
              Il Menù
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, delay: 0.1, ease: EASE }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cream mt-4"
            >
              Oltre 70 pizze, <span className="italic text-gold">una sola passione.</span>
            </motion.h2>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
            className="flex flex-col gap-2 text-cream/50 text-xs tracking-[0.15em] uppercase"
          >
            <span data-testid="menu-maxi-note" className="flex items-center gap-2">
              <Maximize2 size={13} className="text-gold" /> Ogni pizza anche MAXI al doppio del prezzo
            </span>
            <span data-testid="menu-delivery-note" className="flex items-center gap-2">
              <Bike size={13} className="text-gold" /> Consegna a domicilio · € 0,50 a pizza
            </span>
            <span data-testid="menu-dough-note" className="flex items-center gap-2">
              <Wheat size={13} className="text-gold" /> Impasto integrale, ai cereali o senza glutine
            </span>
          </motion.div>
        </div>

        <div data-testid="menu-filters" className="flex flex-wrap gap-x-8 gap-y-4 mt-16 border-b border-white/10 pb-6">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`filter-${c.id}`}
              onClick={() => setCat(c.id)}
              className={`relative text-xs sm:text-sm tracking-[0.2em] uppercase transition-colors duration-300 pb-1 ${
                cat === c.id ? "text-terra" : "text-cream/50 hover:text-cream"
              }`}
            >
              {c.label}
              <motion.span
                className="absolute left-0 -bottom-[25px] h-px w-full bg-terra origin-left"
                initial={false}
                animate={{ scaleX: cat === c.id ? 1 : 0 }}
                transition={{ duration: 0.4, ease: EASE }}
              />
            </button>
          ))}
        </div>

        {cat === "fritte" && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            data-testid="fritte-feature"
            className="mt-14 border border-white/10 bg-ink grid md:grid-cols-2 items-center overflow-hidden"
          >
            <div className="relative flex justify-center py-10">
              <div className="spotlight absolute inset-0" />
              <img
                src="/assets/pizza-fritta.png"
                alt="Pizza fritta al pistacchio di Grano"
                data-testid="fritte-feature-image"
                className="relative w-3/4 max-w-[420px] drop-shadow-[0_30px_50px_rgba(0,0,0,0.65)] hover:scale-[1.03] transition-transform duration-700 ease-out"
              />
            </div>
            <div className="p-8 sm:p-12 border-t md:border-t-0 md:border-l border-white/10">
              <span className="text-[11px] tracking-[0.4em] uppercase text-terra">Fritte al Momento</span>
              <h3 className="font-display text-3xl sm:text-4xl text-cream mt-3">
                Croccanti fuori, <span className="italic text-gold">filanti dentro.</span>
              </h3>
              <p className="text-cream/55 text-sm leading-relaxed mt-5">
                Lo stesso impasto a lunga lievitazione, tuffato in olio bollente solo al momento
                dell'ordine. La tradizione della frittura incontra i sapori di Sicilia.
              </p>
            </div>
          </motion.div>
        )}

        <motion.div layout data-testid="menu-list" className="grid md:grid-cols-2 gap-x-16 gap-y-10 mt-14">
          <AnimatePresence mode="popLayout">
            {items.map((m) => (
              <motion.article
                layout
                key={`${m.cat}-${m.name}`}
                data-testid={`menu-item-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="group"
              >
                <div className="flex items-baseline gap-3">
                  <h3 className="font-display text-xl sm:text-2xl text-cream group-hover:text-gold transition-colors duration-300">
                    {m.name}
                  </h3>
                  {m.price && (
                    <>
                      <span className="dotted-leader flex-1" />
                      <span className="text-gold font-medium whitespace-nowrap">{m.price}</span>
                    </>
                  )}
                </div>
                {m.ing && (
                  <p className="text-cream/45 text-sm leading-relaxed mt-2 pr-8">{m.ing}</p>
                )}
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE }}
          data-testid="componi-panel"
          className="mt-24 border border-white/10 bg-ink p-8 sm:p-12"
        >
          <div className="flex flex-col lg:flex-row gap-12">
            <div className="lg:w-1/3">
              <span className="text-[11px] tracking-[0.4em] uppercase text-terra">Su Misura</span>
              <h3 className="font-display text-3xl sm:text-4xl text-cream mt-3">
                Componi la <span className="italic text-gold">tua pizza</span>
              </h3>
              <p className="text-cream/55 text-sm leading-relaxed mt-5">
                Scegli la base, aggiungi i condimenti che ami. Disponibile anche come pane condito.
              </p>
              <div className="mt-8 flex flex-col gap-3">
                {COMPONI.bases.map((b) => (
                  <div key={b.name} className="flex items-baseline gap-3">
                    <span className="font-display text-lg text-cream">{b.name}</span>
                    <span className="dotted-leader flex-1" />
                    <span className="text-gold">{b.price}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:w-2/3 grid sm:grid-cols-2 gap-8">
              {COMPONI.tiers.map((t) => (
                <div key={t.price} className="border-l border-white/10 pl-6">
                  <span className="font-display text-2xl text-terra">{t.price}</span>
                  <p className="text-cream/50 text-xs sm:text-sm leading-relaxed mt-3">{t.items}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
